import type { SourceGrade } from './contratto';
import type { PhaseQuestionDto } from './fase0';
import type { ConclusionGrade, PhaseCommonDto } from './fasi';

/**
 * La fase 6 «Gli obblighi di legge» del contratto n. 1 versione 2.4 (art. 8, fase 6; art. 3.90-3.97; ADR 0177), nella
 * forma della pagina di prova `docs/mockup/percorso-sinapsi/09-fase6.dc.html`: il registro degli obblighi per area con i
 * conti, ogni obbligo per intero con il «perché?», i «non si applica» con il perché, lo scarto, le cose che aiuterebbero,
 * le domande e il livello.
 */

/** Il grado della conclusione di una voce (art. 3.37): lo stesso delle fasi 1-3. */
export type GradoConclusione = ConclusionGrade;

/** Le aree degli obblighi (art. 3.97), nell'ordine del contratto. */
export const OBLIGATION_AREAS = ['societa_fisco', 'lavoro_previdenza', 'sicurezza_lavoro', 'ambiente_rifiuti', 'prevenzione_incendi', 'protezione_dati', 'edifici_impianti', 'settore'] as const;
export type ObligationArea = (typeof OBLIGATION_AREAS)[number];
export const OBLIGATION_AREA_TEXT: Record<ObligationArea, string> = {
  societa_fisco: 'Società e fisco',
  lavoro_previdenza: 'Lavoro e previdenza',
  sicurezza_lavoro: 'Sicurezza sul lavoro',
  ambiente_rifiuti: 'Ambiente e rifiuti',
  prevenzione_incendi: 'Prevenzione incendi',
  protezione_dati: 'Protezione dei dati',
  edifici_impianti: 'Edifici e impianti',
  settore: 'Obblighi del settore',
};

/** Cosa chiede un obbligo (art. 3.90; allegato, parte C.2, fase 6). */
export const OBLIGATION_ASKS = ['documento', 'registro', 'comunicazione', 'verifica', 'nomina', 'formazione'] as const;
export type ObligationAsk = (typeof OBLIGATION_ASKS)[number];

/** Le fasi che realizzano un obbligo (art. 8, fase 6, Consegna e Bloccanti). */
export const REALIZING_PHASES = [4, 5, 7, 8, 9] as const;

/** Una riga dello scarto (art. 8, «Lo scarto nasce dove nasce la voce»; art. 3.40; allegato, parte C.1). */
export interface ScartoRigaDto {
  /** La voce dell'azienda al suo meglio, come la legge il titolare. */
  voce: string;
  /** ha: le fonti lo mostrano; coperta: lo copre qualcun altro; manca; nessuno: nessuno scarto. */
  oggi: 'ha' | 'coperta' | 'manca' | 'nessuno';
  /** «Da dove lo so», breve: «Visura · A», «Albo gestori ambientali · A»; null = nessuna fonte collegata lo mostra. */
  fonte: string | null;
  /** Una riga in più: chi la copre, cosa manca. */
  perche: string | null;
}

/** Una voce del registro delle domande di una fase già fatta che aiuterebbe qui (art. 7-bis.13). */
export interface AiuterebbeDto {
  id: string;
  voce: string;
  fase: number;
  stato: PhaseQuestionDto['stato'];
  /** Cosa cambierebbe in questa fase: il «cosa cambia» della domanda del registro. */
  cosa_cambierebbe: string;
}


/** Ciò che realizza un obbligo: la fase e il tipo di voce; la voce precisa quando la fase l'ha già consegnata. */
export interface RealizzatoDaDto {
  fase: number;
  tipo: string;
  /** Il nome della voce precisa (un processo della fase 4, un impianto della fase 5); null = la aggancia quella fase. */
  voce: string | null;
}

/** Un obbligo del registro (art. 3.90, 3.96; art. 8, fase 6, Consegna). */
export interface ObbligoDto {
  id: string;
  nome: string;
  descrizione: string | null;
  area: ObligationArea;
  /** si_applica (con il fatto) o non_si_applica (con il perché); «possibile» è il grado. */
  conclusione: 'si_applica' | 'non_si_applica';
  grado: GradoConclusione;
  norma: {
    /** Come si cita: «D.M. 37/2008, art. 7». */
    testo: string;
    /** La frase esatta che impone l'obbligo, dal testo vigente; null se la norma non è verificata. */
    citazione: string | null;
    /** Il testo intero dell'articolo; null se la norma non è verificata. */
    indirizzo_web: string | null;
    /** Lo studio l'ha trovata nel testo vigente (art. 9.1); false = «senza fonte verificata», non si cita. */
    verificata: boolean;
    /** La prassi dell'ente accanto alla norma, quando c'è (art. 5.6). */
    prassi: string | null;
  };
  /** Il fatto che lo fa scattare, con la fase o la fonte; o, se non si applica, il perché. */
  fatto: string;
  /** Il fatto è «da confermare»: l'obbligo è possibile e non si conta tra quelli che si applicano. */
  da_confermare: boolean;
  cosa_chiede: ObligationAsk | null;
  /** «Un documento, con gli allegati»: cosa chiede in parole. */
  cosa_chiede_testo: string | null;
  cadenza: string | null;
  scadenza: string | null;
  ente: string | null;
  piattaforma: string | null;
  sanzione: string | null;
  /** Il tipo di ruolo che serve (art. 3.108): «responsabile: da assegnare». */
  responsabile: string | null;
  realizzato_da: RealizzatoDaDto[];
  scarto: ScartoRigaDto | null;
  /** L'etichetta (art. 3.28): «lo chiede la legge». */
  etichetta: string;
  /** Lo strato da cui viene, o «Azienda». */
  strato: string;
  /** Il «perché?»: il fatto, la norma, il grado e da dove viene (art. 7-bis.8). */
  perche: string;
  /** Una risposta del titolare o una scelta di Timonio portata in questa voce. */
  dal_registro: string | null;
}

/** La pagina della fase 6: le parti comuni (lo scarto delle parti comuni resta vuoto, sta in `scarto_fase`). */
export interface PhaseSixDto extends PhaseCommonDto {
  fase: 6;
  conti: {
    si_applicano: number;
    certi: number;
    probabili: number;
    non_si_applicano: number;
    possibili: number;
    /** Gli obblighi la cui norma lo studio non ha trovato nel testo vigente: fuori dai conti. */
    senza_fonte: number;
  };
  /** I dipendenti che la fase usa, con la fonte (o «da confermare»). */
  dipendenti: { valore: string; fonte: string; da_confermare: boolean };
  aree: Array<{ area: ObligationArea; testo: string; si_applicano: number; non_si_applicano: number; possibili: number }>;
  obblighi: ObbligoDto[];
  /** Lo scarto della fase: per ogni obbligo che si applica, se le fonti mostrano ciò che lo realizza. */
  scarto_fase: ScartoRigaDto[];
  aiuterebbero: AiuterebbeDto[];
  /** La verifica finale spetta ai consulenti esterni dell'azienda (art. 8, fase 6, Consegna; contratto 0). */
  nota_consulenti: string;
}

/** Il grado di una fonte per la pagina, breve: «Visura · A». */
export const sourceChip = (nome: string, grado: SourceGrade | null) => (grado ? `${nome} · ${grado}` : nome);
